import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal, Signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DividerComponent, InstructionsComponent } from '@r1-compass-library/components';
import { FormUnsavedChangesWarningEntity } from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { DivisionConfig, PlayStatus, ScheduleSettings } from '@r1-compass-library/models';
import { Play } from '@r1-compass-library/models/play/play.model';
import { PlayService, SnackBarService } from '@r1-compass-library/services';
import { AssetStateMessageHandlingService } from '@r1-compass-library/services/asset-state-message-handling.service';
import { LocalStorageService } from '@r1-compass-library/services/local-storage.service';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { DateTime } from 'luxon';
import { NgxMatTimepickerComponent, NgxMatTimepickerDirective } from 'ngx-mat-timepicker';
import { filterNil } from 'ngxtension/filter-nil';
import { injectParams } from 'ngxtension/inject-params';
import { catchError, map, of, tap } from 'rxjs';
import { AppConstants } from '../../app.constants';
import { PlayStore } from '../store/play.store';
import { HttpErrorResponse } from '@angular/common/http';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { ActivatedRoute } from '@angular/router';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { provideLuxonDateAdapter, MAT_LUXON_DATE_ADAPTER_OPTIONS } from '@angular/material-luxon-adapter';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { PlayScheduleFormValue, PlaySchedulingFormService } from './play-scheduling.form.service';
import { rxEffect } from 'ngxtension/rx-effect';
import { faCalendarAlt } from '@fortawesome/pro-solid-svg-icons';
import { PlayValidationStateService } from '../play-validation-state.service';

@Component({
    selector: 'r1-compass-app-play-scheduling',
    templateUrl: './play-scheduling.component.html',
    styleUrl: './play-scheduling.component.scss',
    imports: [
        DividerComponent,
        InstructionsComponent,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatSlideToggleModule,
        NgxMatTimepickerComponent,
        NgxMatTimepickerDirective,
        ReactiveFormsModule,
        MatDatepickerModule,
        FontAwesomeModule,
    ],
    providers: [
        provideLuxonDateAdapter(),
        { provide: MAT_LUXON_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
        PlaySchedulingFormService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlaySchedulingComponent implements OnInit, FormUnsavedChangesWarningEntity {
    readonly #store = inject(PlayStore);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #playService = inject(PlayService);
    readonly #localStorageService = inject(LocalStorageService);
    readonly #assetStateMessageHandlingService = inject(AssetStateMessageHandlingService);
    readonly #snackBarService = inject(SnackBarService);
    readonly #formSaveWarningStore = inject(SaveGuardStore);
    readonly #route = inject(ActivatedRoute);
    readonly #playValidationStateService = inject(PlayValidationStateService);
    readonly playSchedulingFormService = inject(PlaySchedulingFormService);

    protected readonly days = AppConstants.days;
    protected readonly offsets = AppConstants.utcOffsets;
    protected readonly today = DateTime.now();

    protected playSchedulingForm = this.playSchedulingFormService.playSchedulingForm;

    public readonly canDeactivate$ = this.#formSaveWarningStore.deriveCanDeactivate$(
        this.playSchedulingFormService.playSchedulingForm
    );
    public readonly saveGuardHandlerFn = this.#formSaveWarningStore.formUnsavedChangesWarningEntityDefinition;
    readonly allowPartialSave = true;

    protected play = this.#store.selectSignal<Play | undefined>(store => store.play);

    /**
     * @description Accounts for the play's persisted validity AND the form service validity
     * We may want to apply this logic bundling to other sections or entities, so this approach
     * is uniform.
     */
    private playValid = computed<boolean>(() => {
        const play = this.play();
        const validInFormService = this.#playValidationStateService.invalidSections().length === 0;
        return (play?.valid ?? false) && validInFormService;
    });

    protected runDisabled = computed<boolean>(() => {
        const play = this.play();
        const playValid = this.playValid();

        if (!playValid) {
            return true;
        }

        if (play) {
            if (play.type === 'Builder') {
                return !play.templateCriteriaId;
            } else if (play.type === 'Importer') {
                return !play.config?.playFileImport?.customFileMapping.length;
            }
        }
        return true;
    });

    protected nextScheduledTime = computed<string>(() => {
        const playSchedule = this.playScheduleFormGroupValue();
        const divisionConfig = this.divisionConfig();
        const playLastRun = DateTime.fromISO(this.play()?.lastRun as unknown as string);
        const transformedPM = this.playSchedulingFormService.transformedPM();
        const playStartDate = this.parseToUtcDateTime(playSchedule.startDate);
        const playEndDate = this.parseToUtcDateTime(playSchedule.endDate);

        return this.deriveNextScheduledTime(
            playSchedule,
            divisionConfig,
            playLastRun,
            transformedPM,
            playStartDate,
            playEndDate
        );
    });

    protected runNowErrorMessage = computed<string | undefined>(() => {
        if (!this.playValid()) {
            return 'This play is invalid and cannot be run. Please review and resolve validation errors and try again.';
        }
        // After development was completed they decided to just have the same message,
        // but I'm leaving these checks in case they change their mind in the future
        const status = this.play()?.status;
        const notPending = Boolean(status) && status !== 'Pending';
        if (notPending || this.onDemandExecutionRequestSuccess()) {
            return 'The Play is currently processing. Wait for the current execution to complete and retry.';
        }
        return undefined;
    });

    protected runNowDisabled = computed<boolean>(
        () => this.runNowErrorMessage() !== undefined || this.onDemandExecutionRequestSuccess()
    );

    protected enableErrorMessage = computed<string | undefined>(() => {
        const playValid = this.playValid();

        return playValid
            ? undefined
            : 'This play is invalid and cannot be enabled. Please review and resolve validation errors and try again.';
    });

    protected runNowMessage = computed<string>(() => {
        const status: PlayStatus | undefined = this.play()?.status;
        const notPending = Boolean(status) && status !== 'Pending';
        if (notPending) {
            return 'Play Processing. Current step: ' + status;
        } else {
            return 'Play Ready';
        }
    });

    protected userHasResetStatusPermission = computed<boolean>(() => {
        const jwt = this.#localStorageService.jwtClaims();
        return jwt?.recs.map(rec => rec.path).includes('/play/resetStatus') ?? false;
    });

    private playScheduleFormGroupValue = this.playSchedulingFormService.playScheduleFormGroupValue;

    private divisionConfig: Signal<DivisionConfig | undefined> = this.#globalLoggedInUserStore.selectedDivisionConfig;
    private playId = injectParams('id');
    private onDemandExecutionRequestSuccess = signal<boolean>(false);

    constructor() {
        const library = inject(FaIconLibrary);
        library.addIcons(faCalendarAlt);

        rxEffect(this.#store.playReset$, () => {
            this.onDemandExecutionRequestSuccess.set(false);
        });

        effect(
            () => {
                const isEnabledControl = this.playSchedulingForm.controls.isEnabled;
                if (this.runDisabled() && isEnabledControl.enabled) {
                    isEnabledControl.disable({ emitEvent: false });
                } else if (!this.runDisabled() && isEnabledControl.disabled) {
                    isEnabledControl.enable({ emitEvent: false });
                }
            },
            { debugName: 'enable/disable isEnabled control based on runDisabled' }
        );

        rxEffect(this.#store.message$.pipe(filterNil()), message => {
            this.#assetStateMessageHandlingService.handleMessage(message);
            this.#store.resetMessage();
        });

        this.saveGuardHandlerFn({
            form: this.playSchedulingForm,
            canDeactivate$: this.canDeactivate$,
            params: this.playId,
            entity$: this.#store.play$,
            savedSoCanRoute$: this.#formSaveWarningStore.savedSoCanRoute$,
            route: this.#route,
        });
    }

    ngOnInit() {
        const playId = this.playId();
        if (playId && playId !== 'new' && this.#store.play()?.id !== Number(playId)) {
            this.#store.getPlay({ id: Number(playId) });
        }
    }

    public onSave() {
        return this.playSchedulingFormService.onSave();
    }

    protected onDateChange(event: MatDatepickerInputEvent<DateTime | string>, type: 'startDate' | 'endDate') {
        if (!event.value) {
            this.playSchedulingFormService.setDate(undefined, type);
            return;
        }

        const dateTime = typeof event.value === 'string' ? DateTime.fromISO(event.value, { zone: 'utc' }) : event.value;
        if (dateTime.isValid) {
            const selectedDate = dateTime.startOf('day').toJSDate();
            this.playSchedulingFormService.setDate(selectedDate, type);
        }
    }

    protected onRun() {
        const playId = this.playId();
        const userId = this.#globalLoggedInUserStore.selectedUserId();
        if (playId && userId) {
            let errorMessage = 'An unknown error occurred.';
            this.#playService
                .runNow(Number(playId), userId)
                .pipe(
                    tap(res => {
                        if (!res.success) {
                            this.openSnackbarForFailedOnDemandExecutionRequest(res.errorMessage ?? errorMessage);
                        }
                    }),
                    map(res => res.success),
                    catchError((err: HttpErrorResponse) => {
                        if (err.error.error && err.error.message) {
                            errorMessage = `${err.error.error}: ${err.error.message}`;
                        }
                        this.openSnackbarForFailedOnDemandExecutionRequest(errorMessage);
                        return of(false);
                    })
                )
                .subscribe(success => this.onDemandExecutionRequestSuccess.set(success));
        }
    }

    protected resetStatus() {
        const playId = this.playId();
        if (playId) {
            this.#store.savePlay({
                id: Number(playId),
                status: 'Pending',
            });
        }
    }

    private openSnackbarForFailedOnDemandExecutionRequest(msg: string) {
        this.#snackBarService.openGenericSnackbar({
            messages: [msg],
            panelClass: 'generic-snack-bar-warning',
            showDismissButton: true,
            verticalPosition: 'top',
        });
    }

    /**
     * Method for calculating next run time of Scheduled Plays.
     * 1. If there is a Play end date and it's in the past, return 'Play end date has passed'.
     * 2. Check if there are any 'checked days', and if there are, add them to Numbered Days for calculation.
     * 3. Check if there is a value in Numbered Days that's equal to today. If yes, proceed to compare dates and times.
     * 4. If current date/time is earlier than run time, list current date at the set time as next run time.
     * 5. If current date/time is later than run time, check for other Numbered Days. If none exist, set next run time
     *        as one week from current date.
     * 6. If Other Numbered Days exist, choose next higher number as the next run time.
     * 7. If no higher numbers exist, choose lowest number as the next run time.
     * @return String comprised of relevant date information to display as "Next Run Time: {UTC datetime | 'On Enable' | $runNowErrorMessage}"
     */
    private deriveNextScheduledTime(
        formValue: PlayScheduleFormValue,
        divisionConfig: DivisionConfig | undefined,
        playLastRun: DateTime,
        transformedPM: string,
        playStartDate?: DateTime,
        playEndDate?: DateTime
    ) {
        const playSchedule = formValue;

        // Determine which schedule to use
        const targetSchedule = playSchedule.override ? playSchedule : divisionConfig?.config?.schedule;

        // If schedule not valid, return default message
        if (!targetSchedule?.daysOfWeek.length || !targetSchedule?.startTime || !targetSchedule?.offset) {
            return playSchedule.override ? 'No valid dates available' : 'Loading...';
        }

        const now = DateTime.utc();
        //If end date is in the past, return not run messages
        if (playEndDate && playEndDate.endOf('day') < now.endOf('day')) {
            return 'Play will not run on schedule due to End Date.';
        }

        if (formValue.isEnabled) {
            return this.getNextRunTimeForPlayToggleEnabled(
                now,
                targetSchedule,
                playLastRun,
                transformedPM,
                formValue.offset,
                playStartDate,
                playEndDate
            );
        } else {
            return '';
        }
    }

    /**
     * Find the next scheduled run time based on the schedule settings.
     * 1. If a start date and is in the future, set search start to start date, else use now.
     * 2. Starting from this date, check to see if that weekday is scheduled.
     * 3. If yes and the play is set to run later in the day (and has not already on that),
     *    and there is no end date or the end date is in the future,
     *    then return that day.
     * 4. Otherwise check the next days (repeat up to 7 days).
     */
    private calculateNextRunTime(
        now: DateTime,
        schedule: ScheduleSettings,
        playLastRun: DateTime,
        transformedPM: string,
        offset: string,
        playStartDate?: DateTime,
        playEndDate?: DateTime
    ): string {
        const searchStart = playStartDate && playStartDate.toMillis() > now.toMillis() ? playStartDate : now;
        let targetDateTime = this.setTimeToUTCScheduledTime(searchStart, schedule.offset!, schedule.startTime!);
        let found = false;
        for (let i = 0; i <= 7; i++) {
            if (
                schedule.daysOfWeek
                    .map(day => day.toLowerCase())
                    .includes(targetDateTime.weekdayLong?.toLowerCase() ?? '')
            ) {
                if (!this.wasLastRunOnDate(playLastRun, targetDateTime) && targetDateTime.toMillis() > now.toMillis()) {
                    if (!playEndDate || targetDateTime.endOf('day') <= playEndDate.endOf('day')) {
                        found = true;
                        break;
                    }
                }
            }
            targetDateTime = targetDateTime.plus({ day: 1 });
        }
        if (!found && playEndDate) {
            return 'Play will not run on schedule due to End Date.';
        }
        return this.createDateString(targetDateTime, transformedPM, offset);
    }

    private createDateString(date: DateTime, transformedPM: string, offset: string) {
        // Can't use `displayOffset` or `displayTime` since they are only updated on save.
        const dateAsUTC = date.toUTC();
        const timeString = transformedPM;
        const offsetString = offset;
        return `${dateAsUTC.weekdayShort} ${dateAsUTC.monthShort} ${dateAsUTC.day} ${dateAsUTC.year} ${timeString}${offsetString}`;
    }

    private getNextRunTimeForPlayToggleEnabled(
        now: DateTime,
        schedule: ScheduleSettings,
        playLastRun: DateTime,
        transformedPM: string,
        offset: string,
        playStartDate?: DateTime,
        playEndDate?: DateTime
    ) {
        const effectiveNow = playStartDate && playStartDate.toMillis() > now.toMillis() ? playStartDate : now;
        const todayIsScheduled = schedule.daysOfWeek
            .map(day => day.toLowerCase())
            .includes(effectiveNow.weekdayLong?.toLowerCase() ?? '');
        const todayWithUTCScheduledTime = this.setTimeToUTCScheduledTime(
            effectiveNow,
            schedule.offset!,
            schedule.startTime!
        );
        const wasLastRunOnDate = this.wasLastRunOnDate(playLastRun, effectiveNow);

        if (
            todayIsScheduled &&
            !wasLastRunOnDate &&
            todayWithUTCScheduledTime.toMillis() < now.toMillis() &&
            (!playStartDate || effectiveNow.toMillis() <= now.toMillis())
        ) {
            return 'On enable'; // Play was scheduled to run earlier today, but was not enabled at that time
        } else {
            return this.calculateNextRunTime(
                now,
                schedule,
                playLastRun,
                transformedPM,
                offset,
                playStartDate,
                playEndDate
            ); // UI toggle is currently on + play was not scheduled to run earlier today
        }
    }

    private setTimeToUTCScheduledTime(dt: DateTime, offset: string, startTime: string) {
        const offsetOperator = offset.at(0);
        let offsetHours = Number(offset.substring(1, 3));
        if (offsetOperator === '+') {
            offsetHours *= -1;
        }
        return dt
            .startOf('day')
            .plus({
                hour: Number(startTime.split(':')[0]),
                minute: Number(startTime.split(':')[1]),
            })
            .plus({ hour: offsetHours });
    }

    private wasLastRunOnDate(lastRun: DateTime, target: DateTime) {
        return lastRun.hasSame(target, 'day') && lastRun.toMillis() <= target.toMillis();
    }

    private parseToUtcDateTime(val: Date | string | undefined): DateTime | undefined {
        if (!val) {
            return undefined;
        }
        if (val instanceof Date) {
            return DateTime.fromJSDate(val).toUTC();
        }
        if (typeof val === 'string') {
            return DateTime.fromISO(val).toUTC();
        }
        return undefined;
    }
}
